import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type {
  FetchPersonalProfileVersionForJobFn,
  FetchSpousalProfileVersionForJobFn,
  FetchDependentProfileVersionForJobFn,
  PersonalProfileEditorViewModelFactory,
  SpousalProfileEditorViewModelFactory,
  DependentProfileEditorViewModelFactory,
  ListDependentsFn,
  PromptDeleteDependentFn,
  FetchPersonalTaxRefundMethodForJobFn,
  FetchBusinessTaxRefundMethodForJobFn,
  TaxRefundMethodEditorViewModelFactory,
  ListTaxRefundMethodsFn,
  PromptDeleteTaxRefundMethodFn,
  BusinessProfileEditorViewModelFactory,
  FetchBusinessProfileForJobFn,
  ListBusinessProfilesFn,
  PromptDeleteBusinessProfileFn,
} from '@/features/tax-profile'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { task } from 'mobx-task'
import { action, computed, observable, runInAction } from 'mobx'
import type { Checklist } from '@/features/jobs/details/tax-type/info-gathering/Checklist'
import { ViewModelList } from '@/infra/view-model-list'
import { PersonalInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/personal-info/PersonalInfoRequestedSupportingItemViewModel'
import { SpousalInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/spousal-info/SpousalInfoRequestedSupportingItemViewModel'
import { SpousalInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/spousal-info/SpousalInfoSupportingItemInput'
import { DependentsInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/DependentsInfoRequestedSupportingItemViewModel'
import { isNotNil } from '@/utils/object-util'
import { PersonalTaxRefundMethodSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/PersonalTaxRefundMethodSupportingItemViewModel'
import { BusinessInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/business-info/BusinessInfoRequestedSupportingItemViewModel'
import { BusinessTaxRefundMethodSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/BusinessTaxRefundMethodSupportingItemViewModel'
import { DocumentRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentRequestedSupportingItemViewModel'
import { NotesRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/notes/NotesRequestedSupportingItemViewModel'
import type {
  DocumentLinkingViewModelFactory,
  DocumentsListViewModelFactory,
} from '@/features/docs'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { TaxInfoGatheringPageViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringPageViewModel'
import type { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { toStream } from '@/utils/mobx-util'
import { distinctUntilChanged, map, tap } from 'rxjs/operators'
import { filter, firstValueFrom } from 'rxjs'
import { fromAbortSignal } from '@/utils/rx-util'
import type { TxfJobViewModel } from '../txf/TxfJobViewModel'
import type { GetCurrentWorkspaceFn } from '@/features/iam'
import { MeasureOneViewModel } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneViewModel'
import type { MeasureOneStore } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneStore'

export type SupportingItemViewModel =
  | PersonalInfoRequestedSupportingItemViewModel
  | SpousalInfoRequestedSupportingItemViewModel
  | DependentsInfoRequestedSupportingItemViewModel
  | PersonalTaxRefundMethodSupportingItemViewModel
  | BusinessInfoRequestedSupportingItemViewModel
  | BusinessTaxRefundMethodSupportingItemViewModel
  | DocumentRequestedSupportingItemViewModel
  | NotesRequestedSupportingItemViewModel

/**
 * Info gathering view model for tax jobs.
 */
export class TaxInfoGatheringViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * List of requested supporting item view models.
   */
  readonly requestedSupportingItemVms

  /**
   * Measure one dedicated view model.
   */
  readonly measureOneVm

  /**
   * The info gathering page view model.
   */
  readonly pageVm

  /**
   * Modal for the Info Gathering completion confirmation.
   */
  readonly completeInfoGatheringConfirmationModalVm = new ModalViewModel()

  /**
   * Modal for displaying the missing required items.
   */
  readonly missingRequiredItemsModalVm = new ModalViewModel()

  /**
   * The current checklist, if any.
   */
  @observable accessor checklist: Checklist | null = null

  /**
   * Stream that emits whether the checklist contains spousal info items.
   */
  readonly isSpousalInfoRequested$ = toStream(
    () => this.isSpousalInfoRequested,
    true,
  )

  /**
   * Emits when info gathering is completed.
   */
  readonly infoGatheringCompleted$ = toStream(
    () => this.job.status !== 'InfoGathering',
    true,
  ).pipe(
    filter(Boolean),
    map(() => this.job),
  )

  constructor(
    public readonly job: SubmittedJob,
    private readonly _infoGatheringStore: InfoGatheringStore,
    createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    createPersonalProfileEditorViewModel: PersonalProfileEditorViewModelFactory,
    fetchPersonalProfileVersionForJob: FetchPersonalProfileVersionForJobFn,
    createSpousalProfileEditorViewModel: SpousalProfileEditorViewModelFactory,
    fetchSpousalProfileVersionForJob: FetchSpousalProfileVersionForJobFn,
    createDependentProfileEditorViewModel: DependentProfileEditorViewModelFactory,
    fetchDependentProfileVersionForJob: FetchDependentProfileVersionForJobFn,
    listDependents: ListDependentsFn,
    promptDeleteDependent: PromptDeleteDependentFn,
    createTaxRefundMethodEditorViewModel: TaxRefundMethodEditorViewModelFactory,
    fetchPersonalTaxRefundMethodForJob: FetchPersonalTaxRefundMethodForJobFn,
    fetchBusinessTaxRefundMethodForJob: FetchBusinessTaxRefundMethodForJobFn,
    listTaxRefundMethods: ListTaxRefundMethodsFn,
    promptDeleteTaxRefundMethod: PromptDeleteTaxRefundMethodFn,
    createBusinessProfileEditorViewModel: BusinessProfileEditorViewModelFactory,
    fetchBusinessProfileForJob: FetchBusinessProfileForJobFn,
    listBusinessProfiles: ListBusinessProfilesFn,
    promptDeleteBusinessProfile: PromptDeleteBusinessProfileFn,
    createDocumentsListVm: DocumentsListViewModelFactory,
    createLinkDocumentsVm: DocumentLinkingViewModelFactory,
    public readonly txfJobVm: TxfJobViewModel,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    newSnackbar: SnackbarFactory,
    private readonly _measureOneStore: MeasureOneStore,
  ) {
    this.requestedSupportingItemVms = new ViewModelList({
      models: () => this.checklist?.allItems ?? [],
      create: (x) =>
        x.match<SupportingItemViewModel>({
          personalInfo: (item) =>
            new PersonalInfoRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createPersonalProfileEditorViewModel,
              fetchPersonalProfileVersionForJob,
            ),
          spousalInfo: (item) =>
            new SpousalInfoRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createSpousalProfileEditorViewModel,
              fetchSpousalProfileVersionForJob,
            ),
          dependentsInfo: (item) =>
            new DependentsInfoRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createDependentProfileEditorViewModel,
              fetchDependentProfileVersionForJob,
              listDependents,
              promptDeleteDependent,
            ),
          personalTaxRefundMethod: (item) =>
            new PersonalTaxRefundMethodSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createTaxRefundMethodEditorViewModel,
              fetchPersonalTaxRefundMethodForJob,
              listTaxRefundMethods,
              promptDeleteTaxRefundMethod,
            ),
          businessInfo: (item) =>
            new BusinessInfoRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createBusinessProfileEditorViewModel,
              fetchBusinessProfileForJob,
              listBusinessProfiles,
              promptDeleteBusinessProfile,
            ),
          businessTaxRefundMethod: (item) =>
            new BusinessTaxRefundMethodSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createTaxRefundMethodEditorViewModel,
              fetchBusinessTaxRefundMethodForJob,
              listTaxRefundMethods,
              promptDeleteTaxRefundMethod,
            ),
          document: (item) =>
            new DocumentRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
              createDocumentsListVm,
              createLinkDocumentsVm,
            ),
          notes: (item) =>
            new NotesRequestedSupportingItemViewModel(
              this.job,
              item,
              this._infoGatheringStore,
              createSnackbarRetryWrapper,
            ),
        }),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
    })

    this._lifecycle.manage(this.requestedSupportingItemVms)

    this.pageVm = new TaxInfoGatheringPageViewModel(this)
    this._lifecycle.manage(this.pageVm)

    this.measureOneVm = new MeasureOneViewModel(
      this.job,
      () => this.checklist,
      this._getCurrentWorkspace,
      this._measureOneStore,
      this._createSnackbarErrorHandler,
      newSnackbar,
    )
    this._lifecycle.manage(this.measureOneVm)

    // Automatically close the required-items dialog when all required items
    // are completed.
    this._lifecycle.manage(
      toStream(() =>
        this.allSupportingItemVms.every(
          (x) => x.isCompleted || !x.item.required,
        ),
      ).pipe(
        filter(Boolean),
        distinctUntilChanged(),
        tap(this.missingRequiredItemsModalVm.close),
      ),
    )
  }

  /**
   * Items specifically for the "your personal information" section.
   */
  @computed get personalInfoSectionItemVms() {
    if (!this.checklist) {
      return []
    }

    return this.checklist.allItems
      .map((item) => {
        const vm = this.requestedSupportingItemVms.get(item)
        switch (vm?.type) {
          case 'PersonalInfo':
          case 'SpousalInfo':
          case 'DependentsInfo':
          case 'PersonalTaxRefundMethod':
            return vm
          default:
            return null
        }
      })
      .filter(isNotNil)
  }

  @computed get isMeasureOneEnabled() {
    return this.measureOneVm.isEnabled
  }

  /**
   * Items specifically for the "business information" section.
   */
  @computed get businessInfoSectionItemVms() {
    if (!this.checklist) {
      return []
    }

    return this.checklist.allItems
      .map((item) => {
        const vm = this.requestedSupportingItemVms.get(item)
        switch (vm?.type) {
          case 'BusinessInfo':
          case 'BusinessTaxRefundMethod':
            return vm
          default:
            return null
        }
      })
      .filter(isNotNil)
  }

  /**
   * Items for requested documents.
   */
  @computed get documentItemVms() {
    if (!this.checklist) {
      return []
    }

    return this.checklist.allItems
      .map((item) => {
        const vm = this.requestedSupportingItemVms.get(item)
        if (vm?.type === 'Document') {
          return vm
        }

        return null
      })
      .filter(isNotNil)
  }

  /**
   * All supporting item view models.
   */
  @computed get allSupportingItemVms() {
    return this.requestedSupportingItemVms.items.filter(isNotNil)
  }

  /**
   * Whether the checklist contains spousal info items.
   */
  @computed
  get isSpousalInfoRequested(): boolean {
    if (!this.checklist) {
      return false
    }

    const spousalItems = this.checklist.getItemsByInputType(
      SpousalInfoSupportingItemInput,
    )

    return spousalItems.length > 0
  }

  /**
   * Gets the completion state of the checklist.
   */
  @computed get completion() {
    switch (this.checklist?.completion.type) {
      case 'Incomplete': {
        const completed: SupportingItemViewModel[] = []
        const incomplete: SupportingItemViewModel[] = []
        let missingRequiredItems = false
        this.allSupportingItemVms.forEach((item) => {
          const target = item.isCompleted ? completed : incomplete
          target.push(item)

          if (!item.isCompleted && item.item.required) {
            missingRequiredItems = true
          }
        })
        return {
          type: 'Incomplete' as const,
          completed,
          incomplete,
          missingRequiredItems,
        }
      }
      case 'Completed':
        return { type: 'Completed' as const, items: this.allSupportingItemVms }
      case 'Empty':
      default:
        return { type: 'Empty' as const }
    }
  }

  @computed
  get showContinueToTaxPrepOrganizer() {
    const infoGatheringCompleted =
      this.completion.type === 'Completed' ||
      (this.completion.type === 'Incomplete' &&
        !this.completion.missingRequiredItems)

    return this.txfJobVm.isEnabledAndTxfJob && infoGatheringCompleted
  }

  /**
   * Whether the checklist is incomplete.
   */
  @computed get isChecklistIncomplete() {
    switch (this.checklist?.completion.type) {
      case 'Incomplete':
        return true
      case 'Completed':
        return false
      case 'Empty':
      default:
        return false
    }
  }

  /**
   * Whether any of the items are currently fetching their content.
   */
  @computed get isFetching() {
    return this.allSupportingItemVms.some((itemVm) => itemVm.fetch.pending)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.missingRequiredItemsModalVm.close()
    this.completeInfoGatheringConfirmationModalVm.close()
  }

  /**
   * Fetches the checklist.
   */
  fetchChecklist = task(async (signal?: AbortSignal) => {
    const checklist = await this._infoGatheringStore.fetchChecklist(
      this.job.id,
      signal,
    )

    signal?.throwIfAborted()

    runInAction(() => {
      // Only set the checklist if it has at least one item.
      this.checklist = checklist?.isPopulated ? checklist : null
    })
  })

  /**
   * Gets the item VM with the specified ID.
   *
   * @param id
   */
  getItemVm(id: Id) {
    return this.allSupportingItemVms.find((vm) => vm.item.id === id) ?? null
  }

  /**
   * Shows the info gathering completion confirmation dialog if
   * there are still missing items, otherwise
   * calls {@link confirmCompleteInfoGathering} immediately.
   */
  @action.bound
  tryCompleteInfoGathering() {
    if (this.completion.type === 'Completed') {
      void this.confirmCompleteInfoGathering()
      return
    }

    if (
      this.completion.type === 'Incomplete' &&
      this.completion.missingRequiredItems
    ) {
      // We're missing some required items.
      this.missingRequiredItemsModalVm.show()
      return
    }

    // Show the confirmation dialog.
    this.completeInfoGatheringConfirmationModalVm.show()
  }

  /**
   * Called when the user has confirmed they wish to proceed.
   */
  confirmCompleteInfoGathering = task.resolved(async () => {
    await this._infoGatheringStore
      .completeInfoGathering(this.job.id)
      .catch(
        this._createSnackbarErrorHandler('Unable to complete info gathering'),
      )

    this.completeInfoGatheringConfirmationModalVm.close()

    // At this point, we know we succeeded, and a navigation to the
    // job details screen is currently pending.
    // To avoid flashing the info gathering UI back to being interactive
    // again, we'll just wait indefinitely here.
    await firstValueFrom(fromAbortSignal(this._lifecycle.signal), {
      defaultValue: null,
    })
  })
}
